document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const IGNOR = window.IGNOR_THEME || {};
const ROOT = IGNOR.root || '/';
const liveRegion = () => document.querySelector('[data-ignor-live-region]');

function announce(message) {
  const region = liveRegion();
  if (!region || !message) return;
  region.textContent = '';
  window.setTimeout(() => { region.textContent = message; }, 30);
}

function formatMoney(cents) {
  const amount = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(amount);
  } catch (_) {
    return `${amount.toFixed(2)} ${window.Shopify?.currency?.active || 'EUR'}`;
  }
}

function escapeHtml(value = '') {
  return String(value).replace(/[&<>'"]/g, char => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  }[char]));
}

function imageWithWidth(source, width) {
  if (!source) return '';
  try {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (_) {
    return `${source}${String(source).includes('?') ? '&' : '?'}width=${encodeURIComponent(width)}`;
  }
}

function isDialogOpen(dialog) {
  return Boolean(dialog && (dialog.open || dialog.hasAttribute('open')));
}

function syncBodyLock() {
  const dialogOpen = [...document.querySelectorAll('dialog.ignor-overlay')].some(isDialogOpen);
  const menuOpen = document.querySelector('[data-menu-toggle][aria-expanded="true"]');
  const filterOpen = document.querySelector('[data-filter-panel].is-open');
  document.documentElement.classList.toggle('ignor-drawer-open', Boolean(dialogOpen || menuOpen || filterOpen));
}

function closeDialog(dialog, { restoreFocus = true } = {}) {
  if (!dialog) return;
  if (typeof dialog.close === 'function' && dialog.open) dialog.close();
  else dialog.removeAttribute('open');
  syncBodyLock();
  if (restoreFocus) dialog._ignorOpener?.focus?.();
}

function openDialog(dialog, opener) {
  if (!dialog) return;
  document.querySelectorAll('dialog.ignor-overlay').forEach(other => {
    if (other !== dialog && isDialogOpen(other)) closeDialog(other, { restoreFocus: false });
  });
  dialog._ignorOpener = opener || document.activeElement;
  if (typeof dialog.showModal === 'function') {
    if (!dialog.open) dialog.showModal();
  } else {
    dialog.setAttribute('open', '');
  }
  syncBodyLock();
  const focusable = dialog.querySelector('input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]');
  focusable?.focus();
}

function closeOpenFilter({ restoreFocus = true } = {}) {
  const panel = document.querySelector('[data-filter-panel].is-open');
  if (!panel) return false;
  const facets = panel.closest('ignor-facets');
  facets?.closeFilter?.({ restoreFocus });
  if (!facets) {
    panel.classList.remove('is-open');
    syncBodyLock();
  }
  return true;
}

class IgnorMenu extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    this.button = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    if (!this.button || !this.panel) return;
    this.label = this.button.querySelector('.ignor-visually-hidden');

    this.button.addEventListener('click', () => this.toggle());
    this.panel.addEventListener('click', event => {
      if (!event.target.closest('a[href]')) return;
      this.close({ restoreFocus: false });
    });
    this.syncLabel();
  }

  get isOpen() {
    return this.button?.getAttribute('aria-expanded') === 'true';
  }

  syncLabel() {
    if (this.label) this.label.textContent = this.isOpen ? 'Close menu' : 'Open menu';
  }

  open() {
    if (this.isOpen) return;
    this.button.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
    document.querySelector('[data-ignor-header]')?.classList.add('is-menu-open');
    this.syncLabel();
    syncBodyLock();
    window.requestAnimationFrame(() => this.panel.querySelector('a[href]')?.focus());
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    document.querySelector('[data-ignor-header]')?.classList.remove('is-menu-open');
    this.syncLabel();
    syncBodyLock();
    if (restoreFocus) this.button.focus();
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }
}
if (!customElements.get('ignor-menu')) customElements.define('ignor-menu', IgnorMenu);

// ---------------- Cart drawer ----------------
const cartDialog = document.querySelector('[data-cart-dialog]');
const cartBody = document.querySelector('[data-cart-drawer-body]');
const cartFooter = document.querySelector('[data-cart-drawer-footer]');
let cartBusy = false;

async function getCart() {
  const response = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Unable to load cart.');
  return response.json();
}

function updateCartIndicators(count) {
  document.querySelectorAll('[data-cart-count]').forEach(element => {
    element.textContent = count;
  });
  document.querySelectorAll('[data-cart-open]').forEach(button => {
    button.setAttribute('aria-label', `Open cart, ${count} ${count === 1 ? 'item' : 'items'}`);
  });
}

function setCartStatus(message = '', isError = false) {
  if (!cartBody) return;
  let status = cartBody.querySelector('[data-cart-status]');
  if (!status) {
    status = document.createElement('p');
    status.className = 'ignor-cart-status';
    status.dataset.cartStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    cartBody.prepend(status);
  }
  status.textContent = message;
  status.classList.toggle('is-error', isError);
  status.hidden = !message;
}

function setCartBusy(busy) {
  cartBusy = busy;
  cartDialog?.classList.toggle('is-busy', busy);
  cartDialog?.setAttribute('aria-busy', String(busy));
  cartBody?.querySelectorAll('[data-cart-change]').forEach(button => {
    button.disabled = busy;
  });
}

function renderCart(cart) {
  updateCartIndicators(cart.item_count);
  if (!cartBody || !cartFooter) return;

  if (!cart.item_count) {
    cartBody.innerHTML = `
      <div class="ignor-empty-state">
        <h3>YOUR CART IS EMPTY.</h3>
        <a class="ignor-button ignor-button--dark" href="${escapeHtml(IGNOR.allProductsUrl || `${ROOT}collections/all`)}">SHOP ALL</a>
      </div>`;
    cartFooter.hidden = true;
    return;
  }

  cartBody.innerHTML = cart.items.map((item, index) => `
    <article class="ignor-cart-drawer__item" data-cart-line="${index + 1}">
      <a class="ignor-cart-drawer__image" href="${escapeHtml(item.url)}">
        ${item.image ? `<img src="${escapeHtml(imageWithWidth(item.image, 240))}" alt="${escapeHtml(item.product_title)}" loading="lazy">` : ''}
      </a>
      <div class="ignor-cart-drawer__item-body">
        <a class="ignor-cart-drawer__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="ignor-cart-drawer__variant">${escapeHtml(item.variant_title)}</p>` : ''}
        <div class="ignor-cart-drawer__quantity">
          <button type="button" data-cart-change="${index + 1}" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
          <span>${item.quantity}</span>
          <button type="button" data-cart-change="${index + 1}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          <button class="ignor-text-button" type="button" data-cart-change="${index + 1}" data-cart-quantity="0">Remove</button>
        </div>
      </div>
      <strong>${formatMoney(item.final_line_price)}</strong>
    </article>`).join('');

  cartFooter.hidden = false;
  const subtotal = cartFooter.querySelector('[data-cart-subtotal]');
  if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
}

async function refreshCart(open = false) {
  try {
    const cart = await getCart();
    renderCart(cart);
    if (open) openDialog(cartDialog);
  } catch (error) {
    if (cartBody) {
      cartBody.innerHTML = '';
      setCartStatus(error.message || 'Unable to load cart.', true);
    }
  }
}

async function changeCartLine(line, quantity) {
  if (cartBusy) return;
  setCartBusy(true);
  setCartStatus('Updating cart…');
  try {
    const response = await fetch(`${ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.description || 'Unable to update cart.');
    renderCart(data);
    announce('Cart updated.');
  } catch (error) {
    setCartStatus(error.message || 'Unable to update cart.', true);
    announce(error.message || 'Unable to update cart.');
  } finally {
    setCartBusy(false);
  }
}

document.addEventListener('click', event => {
  const cartOpen = event.target.closest('[data-cart-open]');
  if (cartOpen) {
    event.preventDefault();
    refreshCart(true);
    return;
  }

  if (event.target.closest('[data-cart-close]')) {
    closeDialog(cartDialog);
    return;
  }

  const change = event.target.closest('[data-cart-change]');
  if (change) {
    event.preventDefault();
    if (change.disabled || cartBusy) return;
    changeCartLine(Number(change.dataset.cartChange), Number(change.dataset.cartQuantity));
  }
});

cartDialog?.addEventListener('click', event => {
  if (event.target === cartDialog) closeDialog(cartDialog);
});

// ---------------- Product variant system ----------------
class IgnorProductForm extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    this.form = this.matches('form') ? this : this.querySelector('form');
    if (!this.form) return;

    this.root = this.closest('[data-product-root]');
    this.variantData = JSON.parse(this.form.querySelector('[data-product-variants]')?.textContent || '[]');
    this.variantInput = this.form.querySelector('[data-variant-id]');
    this.submit = this.form.querySelector('[data-add-to-cart]');
    this.message = this.form.querySelector('[data-product-form-message]');
    this.groups = [...this.form.querySelectorAll('[data-variant-option-group]')];
    this.selected = new Array(this.groups.length).fill(null);

    this.groups.forEach(group => {
      const index = Number(group.dataset.optionIndex);
      const selectedButton = group.querySelector('.is-selected');
      if (selectedButton) this.selected[index] = selectedButton.dataset.optionValue;

      group.addEventListener('click', event => {
        const button = event.target.closest('[data-option-value]');
        if (!button || button.disabled) return;
        this.selectOption(index, button.dataset.optionValue);
      });
    });

    this.form.addEventListener('submit', event => this.onSubmit(event));
    this.syncButtons();
    this.updateVariant();
  }

  matchesSelection(variant, selection = this.selected, ignoreIndex = -1) {
    return selection.every((value, index) => index === ignoreIndex || value === null || variant.options[index] === value);
  }

  selectOption(index, value) {
    this.selected[index] = value;

    while (!this.variantData.some(variant => this.matchesSelection(variant))) {
      const conflictingIndex = this.selected.findIndex((selectedValue, selectedIndex) => {
        if (selectedIndex === index || selectedValue === null) return false;
        const test = [...this.selected];
        test[selectedIndex] = null;
        return this.variantData.some(variant => this.matchesSelection(variant, test));
      });
      if (conflictingIndex === -1) break;
      this.selected[conflictingIndex] = null;
    }

    this.groups.forEach((group, groupIndex) => {
      group.querySelector('[data-option-error]')?.setAttribute('hidden', '');
      if (groupIndex === index && this.message) this.message.textContent = '';
    });
    this.message?.classList.remove('is-error');
    this.syncButtons();
    this.updateVariant();
  }

  syncButtons() {
    this.groups.forEach((group, groupIndex) => {
      group.querySelectorAll('[data-option-value]').forEach(button => {
        const value = button.dataset.optionValue;
        const isSelected = this.selected[groupIndex] === value;
        const matchingVariants = this.variantData.filter(variant => {
          if (variant.options[groupIndex] !== value) return false;
          return this.matchesSelection(variant, this.selected, groupIndex);
        });
        const exists = matchingVariants.length > 0;
        const available = matchingVariants.some(variant => variant.available);
        const disabled = !exists || !available;

        button.classList.toggle('is-selected', isSelected);
        button.classList.toggle('is-unavailable', disabled);
        button.setAttribute('aria-pressed', String(isSelected));
        button.setAttribute('aria-disabled', String(disabled));
        button.disabled = disabled && !isSelected;
      });
    });
  }

  currentVariant() {
    if (this.variantData.length === 0) {
      const id = Number(this.variantInput?.value);
      return id ? { id, available: true } : null;
    }
    if (this.selected.some(value => value === null)) return null;
    return this.variantData.find(variant => variant.options.every((value, index) => value === this.selected[index])) || null;
  }

  firstMissingGroup() {
    return this.groups.find((_, index) => this.selected[index] === null) || null;
  }

  missingActionLabel() {
    const sizeIndex = this.groups.findIndex(group => group.hasAttribute('data-size-group'));
    if (sizeIndex >= 0 && this.selected[sizeIndex] === null) return 'SELECT SIZE';
    return 'SELECT OPTIONS';
  }

  updateVariant() {
    const variant = this.currentVariant();
    const sticky = document.querySelector('[data-sticky-action]');

    if (variant) {
      if (this.variantInput) this.variantInput.value = variant.id;
      this.updatePrice(variant);
      this.updateMedia(variant);
      if (this.submit) {
        this.submit.disabled = !variant.available;
        this.submit.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
      }
      if (sticky) {
        sticky.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
        sticky.disabled = !variant.available;
      }
      return;
    }

    if (this.variantInput) this.variantInput.value = '';
    const allUnavailable = this.variantData.length > 0 && !this.variantData.some(item => item.available);
    if (this.submit && this.variantData.length) {
      this.submit.disabled = allUnavailable;
      this.submit.textContent = allUnavailable ? 'SOLD OUT' : 'ADD TO CART';
    }
    if (sticky) {
      sticky.textContent = allUnavailable ? 'SOLD OUT' : this.missingActionLabel();
      sticky.disabled = allUnavailable;
    }
  }

  updatePrice(variant) {
    const formatted = formatMoney(variant.price);
    this.root?.querySelector('[data-product-price]')?.replaceChildren(document.createTextNode(formatted));
    document.querySelector('[data-sticky-price]')?.replaceChildren(document.createTextNode(formatted));
  }

  updateMedia(variant) {
    const mediaId = variant.featured_media?.id;
    if (!mediaId) return;
    const media = this.root?.querySelector(`[data-media-id="${mediaId}"]`);
    if (!media) return;
    this.root.querySelectorAll('[data-media-id]').forEach(item => {
      item.classList.toggle('is-variant-media', item === media);
    });
    if (window.matchMedia('(max-width: 767px)').matches) {
      media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  validateOptions() {
    let valid = true;
    this.groups.forEach((group, index) => {
      const missing = this.selected[index] === null;
      if (missing) valid = false;
      group.querySelector('[data-option-error]')?.toggleAttribute('hidden', !missing);
    });

    if (!valid) {
      this.firstMissingGroup()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return valid;
  }

  async onSubmit(event) {
    event.preventDefault();
    this.message?.classList.remove('is-error');

    if (!this.validateOptions()) {
      if (this.message) this.message.textContent = 'Please select all required options.';
      return;
    }

    const variant = this.currentVariant();
    if (!variant?.available) {
      if (this.message) {
        this.message.textContent = 'This variant is unavailable.';
        this.message.classList.add('is-error');
      }
      return;
    }

    if (this.submit) {
      this.submit.disabled = true;
      this.submit.textContent = 'ADDING…';
    }
    if (this.message) this.message.textContent = '';

    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add this design to cart.');
      await refreshCart(true);
      announce(`${data.product_title || 'Product'} added to cart.`);
      if (this.message) this.message.textContent = 'Added to cart.';
    } catch (error) {
      if (this.message) {
        this.message.textContent = error.message;
        this.message.classList.add('is-error');
      }
    } finally {
      this.updateVariant();
    }
  }
}

// Upgrade product forms created by Liquid without changing the form element name.
document.querySelectorAll('form[data-ignor-product-form]').forEach(form => {
  if (form.dataset.ignorUpgraded) return;
  form.dataset.ignorUpgraded = 'true';
  const controller = document.createElement('ignor-product-form');
  form.parentNode.insertBefore(controller, form);
  controller.appendChild(form);
});
if (!customElements.get('ignor-product-form')) customElements.define('ignor-product-form', IgnorProductForm);

// ---------------- Size guide ----------------
const sizeDialog = document.querySelector('[data-size-guide-dialog]');
document.addEventListener('click', event => {
  const open = event.target.closest('[data-size-guide-open]');
  if (open) {
    event.preventDefault();
    openDialog(sizeDialog, open);
    return;
  }
  if (event.target.closest('[data-size-guide-close]')) closeDialog(sizeDialog);
});
sizeDialog?.addEventListener('click', event => {
  if (event.target === sizeDialog) closeDialog(sizeDialog);
});

// ---------------- Sticky mobile purchase ----------------
const purchasePanel = document.querySelector('.ignor-purchase-panel');
const stickyPurchase = document.querySelector('[data-sticky-purchase]');
if (purchasePanel && stickyPurchase) {
  const observer = new IntersectionObserver(entries => {
    const show = !entries[0].isIntersecting && window.matchMedia('(max-width: 767px)').matches;
    stickyPurchase.hidden = !show;
  }, { threshold: 0.05 });
  observer.observe(purchasePanel);

  stickyPurchase.querySelector('[data-sticky-action]')?.addEventListener('click', () => {
    const form = document.querySelector('form[data-ignor-product-form]');
    const controller = form?.closest('ignor-product-form');
    const variantId = form?.querySelector('[data-variant-id]')?.value;
    if (variantId) {
      form.requestSubmit();
      return;
    }
    const target = controller?.firstMissingGroup?.() || document.querySelector('[data-size-group], [data-variant-option-group]');
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

// ---------------- Product gallery counter ----------------
let galleryObserver = null;
const gallery = document.querySelector('[data-product-gallery]');
const galleryMediaQuery = window.matchMedia('(max-width: 767px)');

function setupGalleryCounter() {
  galleryObserver?.disconnect();
  galleryObserver = null;
  if (!gallery || !galleryMediaQuery.matches) return;

  const items = [...gallery.querySelectorAll('[data-media-id]')];
  const counter = gallery.querySelector('[data-gallery-counter]');
  if (!items.length || !counter) return;

  galleryObserver = new IntersectionObserver(entries => {
    const visible = entries
      .filter(entry => entry.isIntersecting)
      .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
    if (!visible) return;
    const index = items.indexOf(visible.target);
    counter.textContent = `${index + 1} / ${items.length}`;
  }, { root: gallery, threshold: [0.55, 0.75, 1] });

  items.forEach(item => galleryObserver.observe(item));
}
setupGalleryCounter();
galleryMediaQuery.addEventListener?.('change', setupGalleryCounter);

// ---------------- Predictive search ----------------
const searchDialog = document.querySelector('[data-search-dialog]');
const predictiveInput = document.querySelector('[data-predictive-search-input]');
const predictiveResults = document.querySelector('[data-predictive-search-results]');
let predictiveAbort = null;
let searchTimer = null;

function filterStaticSearch(term) {
  const normalized = term.trim().toLowerCase();
  document.querySelectorAll('[data-search-static-item]').forEach(item => {
    item.hidden = normalized.length < 2 || !item.dataset.searchText.includes(normalized);
  });
  document.querySelectorAll('[data-search-static-group]').forEach(group => {
    group.hidden = ![...group.querySelectorAll('[data-search-static-item]')].some(item => !item.hidden);
  });
}

function setPredictiveBusy(busy) {
  predictiveResults?.setAttribute('aria-busy', String(busy));
}

async function runPredictiveSearch(term) {
  filterStaticSearch(term);
  if (!predictiveResults) return;

  const normalized = term.trim();
  if (normalized.length < 2) {
    predictiveAbort?.abort();
    predictiveResults.innerHTML = '';
    setPredictiveBusy(false);
    return;
  }

  predictiveAbort?.abort();
  predictiveAbort = new AbortController();
  setPredictiveBusy(true);
  predictiveResults.innerHTML = '<p class="ignor-search-loading" role="status">Searching…</p>';

  const base = IGNOR.predictiveSearchUrl || `${ROOT}search/suggest`;
  const url = `${base}?q=${encodeURIComponent(normalized)}&section_id=ignor-predictive-search&resources[type]=product,page,collection,query&resources[limit]=6`;

  try {
    const response = await fetch(url, { signal: predictiveAbort.signal });
    if (!response.ok) throw new Error('Search unavailable.');
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('#shopify-section-ignor-predictive-search');
    predictiveResults.innerHTML = section?.innerHTML || html;
  } catch (error) {
    if (error.name !== 'AbortError') {
      predictiveResults.innerHTML = '<p class="ignor-form-message is-error">Search is temporarily unavailable.</p>';
    }
  } finally {
    setPredictiveBusy(false);
  }
}

predictiveInput?.addEventListener('input', event => {
  clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => runPredictiveSearch(event.target.value), 180);
});

document.addEventListener('click', event => {
  const open = event.target.closest('[data-search-open]');
  if (open) {
    event.preventDefault();
    openDialog(searchDialog, open);
    predictiveInput?.focus();
    return;
  }
  if (event.target.closest('[data-search-close]')) closeDialog(searchDialog);
});
searchDialog?.addEventListener('click', event => {
  if (event.target === searchDialog) closeDialog(searchDialog);
});

// ---------------- Collection facets ----------------
class IgnorFacets extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    this.requestController = null;
    this.requestSequence = 0;
    this.filterOpener = null;

    this.addEventListener('submit', event => {
      if (!event.target.matches('[data-facet-form]')) return;
      event.preventDefault();
      this.renderFromForm(event.target);
    });

    this.addEventListener('change', event => {
      if (!event.target.matches('[data-sort-select]')) return;
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      this.renderUrl(url);
    });

    this.addEventListener('click', event => {
      const open = event.target.closest('[data-filter-open]');
      if (open) {
        event.preventDefault();
        this.openFilter(open);
        return;
      }

      const close = event.target.closest('[data-filter-close]');
      if (close) {
        event.preventDefault();
        this.closeFilter();
        return;
      }

      const ajaxLink = event.target.closest('.ignor-active-filters a, .ignor-facets__actions a, .ignor-pagination a');
      if (ajaxLink?.href && ajaxLink.origin === window.location.origin) {
        event.preventDefault();
        this.renderUrl(new URL(ajaxLink.href));
      }
    });

    window.addEventListener('popstate', () => this.renderUrl(new URL(window.location.href), false));
  }

  openFilter(opener) {
    const panel = this.querySelector('[data-filter-panel]');
    if (!panel) return;
    this.filterOpener = opener || document.activeElement;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    syncBodyLock();
    window.requestAnimationFrame(() => panel.querySelector('[data-filter-close]')?.focus());
  }

  closeFilter({ restoreFocus = true } = {}) {
    const panel = this.querySelector('[data-filter-panel]');
    if (!panel?.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    syncBodyLock();
    if (restoreFocus) this.filterOpener?.focus?.();
  }

  renderFromForm(form) {
    const url = new URL(window.location.pathname, window.location.origin);
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    });
    const sort = this.querySelector('[data-sort-select]')?.value;
    if (sort) url.searchParams.set('sort_by', sort);
    this.renderUrl(url);
  }

  async renderUrl(url, push = true) {
    const sequence = ++this.requestSequence;
    this.requestController?.abort();
    this.requestController = new AbortController();
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');

    const sectionId = this.dataset.sectionId;
    const sectionUrl = new URL(url.toString());
    sectionUrl.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(sectionUrl.toString(), { signal: this.requestController.signal });
      if (!response.ok) throw new Error('Unable to update products.');
      const html = await response.text();
      if (sequence !== this.requestSequence) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const replacement = doc.querySelector('ignor-facets');
      if (!replacement) throw new Error('Unable to update products.');

      this.innerHTML = replacement.innerHTML;
      if (push) history.pushState({}, '', url);
      this.closeFilter({ restoreFocus: false });
      syncBodyLock();
      announce('Product results updated.');
    } catch (error) {
      if (error.name !== 'AbortError') announce(error.message || 'Unable to update products.');
    } finally {
      if (sequence === this.requestSequence) {
        this.classList.remove('is-loading');
        this.setAttribute('aria-busy', 'false');
      }
    }
  }
}
if (!customElements.get('ignor-facets')) customElements.define('ignor-facets', IgnorFacets);

// ---------------- Recently viewed ----------------
const recentProductNode = document.querySelector('[data-recent-product-json]');
if (recentProductNode) {
  try {
    const current = JSON.parse(recentProductNode.textContent);
    const stored = JSON.parse(localStorage.getItem('ignorRecentlyViewed') || '[]')
      .filter(item => item.handle !== current.handle);
    localStorage.setItem('ignorRecentlyViewed', JSON.stringify([current, ...stored].slice(0, 12)));
  } catch (_) {}
}

const recentSection = document.querySelector('[data-recently-viewed-section]');
if (recentSection) {
  try {
    const currentHandle = document.querySelector('[data-product-root]')?.dataset.productHandle;
    const recent = JSON.parse(localStorage.getItem('ignorRecentlyViewed') || '[]')
      .filter(item => item.handle !== currentHandle)
      .slice(0, 4);

    if (recent.length) {
      const grid = recentSection.querySelector('[data-recently-viewed-grid]');
      grid.innerHTML = recent.map(item => {
        const media = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">`
          : `<div class="ignor-product-card__fallback"><img src="${escapeHtml(IGNOR.brandIconUrl || '')}" alt="" aria-hidden="true"><span>ARTWORK IN PREPARATION</span></div>`;
        return `
          <article class="ignor-product-card">
            <a href="${escapeHtml(item.url)}">
              <div class="ignor-product-card__media${item.image ? '' : ' ignor-product-card__media--fallback'}">${media}</div>
              <div class="ignor-product-card__body">
                <h3 class="ignor-product-card__title">${escapeHtml(item.title)}</h3>
                ${item.world ? `<div class="ignor-product-card__meta">IGNOR ${escapeHtml(item.world)}</div>` : ''}
                <div class="ignor-product-card__price">${escapeHtml(item.price)}</div>
              </div>
            </a>
          </article>`;
      }).join('');
      recentSection.hidden = false;
    }
  } catch (_) {}
}

// ---------------- Global keyboard and state handling ----------------
document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;

  const openDialogElement = [...document.querySelectorAll('dialog.ignor-overlay')]
    .reverse()
    .find(isDialogOpen);
  if (openDialogElement) {
    closeDialog(openDialogElement);
    return;
  }

  if (closeOpenFilter()) return;

  const menu = document.querySelector('ignor-menu');
  if (menu?.isOpen) menu.close();
});

document.addEventListener('DOMContentLoaded', () => {
  syncBodyLock();
  refreshCart(false);
});
